import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import api from '../api';
import { CoworkState } from '../types/cowork';

function getSocketBase() {
  const base = process.env.REACT_APP_API_URL || 'http://localhost:5010/api';
  return base.replace(/\/api\/?$/, '') || window.location.origin;
}

/**
 * Estado del módulo Cowork en el admin. El backend es la fuente de verdad:
 * las acciones devuelven el estado nuevo y el socket lo replica al resto.
 */
export function useCoworkAdmin() {
  const [state, setState] = useState<CoworkState | null>(null);
  const [overlayKey, setOverlayKey] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [now, setNow] = useState(() => Date.now());

  const offsetRef = useRef(0);

  const applyState = useCallback((next: CoworkState) => {
    setState(next);
    offsetRef.current = Date.parse(next.serverNow) - Date.now();
    if (next.overlayKey) setOverlayKey(next.overlayKey);
  }, []);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get<CoworkState>('/cowork/state');
      applyState(data);
      setError('');
    } catch (err: any) {
      setError(err?.response?.data?.message || 'No se pudo cargar Cowork');
    } finally {
      setLoading(false);
    }
  }, [applyState]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (!overlayKey) return undefined;

    const socket: Socket = io(getSocketBase(), { path: '/api/socket.io' });
    socket.on('connect', () => socket.emit('join-cowork', { key: overlayKey }));
    socket.on('cowork:state', (next: CoworkState) => applyState({ ...next, overlayKey }));

    return () => {
      socket.emit('leave-cowork', { key: overlayKey });
      socket.disconnect();
    };
  }, [overlayKey, applyState]);

  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 250);
    return () => window.clearInterval(id);
  }, []);

  const remainingMs = useMemo(() => {
    const timer = state?.timer;
    if (!timer) return 0;
    if (timer.status === 'running' && timer.phaseEndsAt) {
      return Math.max(0, Date.parse(timer.phaseEndsAt) - (now + offsetRef.current));
    }
    return timer.remainingMs;
  }, [state, now]);

  const run = useCallback(
    async (request: Promise<{ data: any }>) => {
      try {
        const { data } = await request;
        if (data && data.timer) applyState(data);
        else await refresh();
        setError('');
        return true;
      } catch (err: any) {
        setError(err?.response?.data?.message || 'No se pudo completar la acción');
        return false;
      }
    },
    [applyState, refresh],
  );

  return { state, overlayKey, remainingMs, loading, error, setError, refresh, run };
}

export function formatClock(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

export function formatDuration(ms: number): string {
  const totalMinutes = Math.round(ms / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours > 0) return `${hours} h ${minutes} min`;
  return `${minutes} min`;
}
